import NavBar from "../../components/NavBar/NavBar";
import Footer from "../../components/Footer/Footer";
import Button from "../../components/Button/Button";
import Checkmark from "../../components/Checkmark/Checkmark";
import { productDetails } from "../SignUpPage/productDetails";
import "./ProductPage.scss";
import heroFrcophthTopics from "../../assets/oq/hero-frcophth-topics.png";
import frcophth1QuestionsPage from "../../assets/products/frcophth1-questions-page.png";
import frcophth1ModulesPage from "../../assets/products/frcophth1-modules-page.png";
import frcophth1ModuleProgress from "../../assets/products/frcophth1-module-progress.png";
import frcophth1TimedExamPage from "../../assets/products/frcophth1-timed-exam-page.png";
import { FaCheckCircle, FaBook, FaStar, FaBrain, FaChartLine, FaBullseye } from "react-icons/fa";
import { topicIcons } from "./topicIcons";

interface FRCOphthPart1PageProps {
  product: {
    id: string;
    price: number;
    length: string;
    comingSoon: boolean;
  };
}

const FRCOphthPart1Page = ({ product }: FRCOphthPart1PageProps) => {
  const details = productDetails[product.id];

  return (
    <>
      <NavBar />
      <div id="ProductPage" className="product-page--oq">
        <section className="oq-product-hero" aria-label="FRCOphth Part 1">
          <div className="site-container oq-product-hero__inner">
            <div className="oq-product-hero__copy">
              <p className="oq-product-hero__eyebrow">FRCOphth Part 1</p>
              <h1 className="oq-product-hero__title">FRCOphth Part 1 Question Bank</h1>
              <p className="oq-product-hero__lead">
                3,750+ exam-style MCQs covering <strong>every module and every syllabus area</strong>—wording and format
                matched to real papers. Includes 500+ high-yield favourites, <strong>timed mock exams</strong>, and
                detailed analytics. <strong>Co-built by ophthalmologists, for ophthalmologists.</strong>
              </p>
              <div className="oq-product-hero__pricing">
                <span className="oq-product-hero__price">£{product.price}</span>
                <span className="oq-product-hero__length">/ {product.length}</span>
              </div>
              {!product.comingSoon && (
                <div className="oq-product-hero__signup">
                  <Button
                    text="Buy FRCOphth Part 1 Now"
                    linkTo={`/sign-up?product=${product.id}`}
                    type="navy"
                  />
                </div>
              )}
            </div>
            <div className="oq-product-hero__visual">
              <div className="oq-product-hero__frame">
                <img
                  className="oq-product-hero__image"
                  src={heroFrcophthTopics}
                  alt="FRCOphth Part 1 Topics dashboard with modules and progress in OphthalmologyQ"
                  width={1024}
                  height={732}
                  loading="eager"
                  decoding="async"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <div className="product-sales-content">
          <div className="site-container">
            
            {/* Introduction Section */}
            <section className="sales-section intro-section">
              <p className="lead-text">
                The FRCOphth Part 1 syllabus is vast, detailed, and heavily knowledge-based. Many candidates struggle
                not from lack of effort—but from revising without enough exposure to questions that feel like the real
                exam in <strong>wording, format, and difficulty</strong>.
              </p>
              <p className="solution-text">
                <strong>OphthalmologyQ is co-built by ophthalmologists for ophthalmologists</strong>—so every question
                is written with clinical and exam realism in mind.
              </p>
            </section>

            {/* Questions Section */}
            <section className="sales-section questions-section">
              <div className="content-with-image">
                <div className="content-side">
                  <div className="section-header">
                    <FaBook className="section-icon" />
                    <h2>3,750+ High-Quality MCQs</h2>
                  </div>
                  <p className="lead-text">
                    Practise with 3,750+ carefully written MCQs that map to <strong>every module and every area</strong>{" "}
                    of the Part 1 syllabus—so you are not caught out by an untested topic on exam day.
                  </p>
                  <div className="topics-list">
                    <h3>Complete Syllabus Coverage:</h3>
                    <div className="topics-grid">
                      {details?.topicsCovered?.map((topic, index) => (
                        <div key={index} className="topic-card">
                          <div className="topic-icon">{topicIcons[topic]}</div>
                          <span className="topic-name">{topic}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <p className="tagline-text">
                    Every topic tested. No gaps. No wasted time.
                  </p>
                </div>
                <div className="image-side">
                  <img src={frcophth1ModulesPage} alt="FRCOphth Part 1 Question Bank Modules" className="product-screenshot" />
                </div>
              </div>
            </section>

            {/* High-Yield Questions Section - Coming Soon (match Part 2 style) */}
            <section className="sales-section highlight-section">
              <div className="highlight-header">
                <div className="highlight-badge highlight-badge-coming-soon">
                  <FaStar className="star-icon" />
                  <span>Coming Soon</span>
                </div>
                <h2>500+ High-Yield Exam Questions (Coming Soon)</h2>
              </div>
              <p className="lead-text">
                A dedicated collection of 500+ high-yield MCQs focused on the topics that matter most for Part 1 success will be available soon:
              </p>
              <div className="highlight-features">
                <div className="highlight-feature-card">
                  <FaCheckCircle className="highlight-icon" />
                  <div>
                    <h3>Commonly examined topics</h3>
                    <p>Questions covering the most frequently tested areas</p>
                  </div>
                </div>
                <div className="highlight-feature-card">
                  <FaCheckCircle className="highlight-icon" />
                  <div>
                    <h3>Repeated exam themes</h3>
                    <p>Patterns and concepts that appear year after year</p>
                  </div>
                </div>
                <div className="highlight-feature-card">
                  <FaCheckCircle className="highlight-icon" />
                  <div>
                    <h3>High-impact differentiators</h3>
                    <p>The questions that separate successful candidates</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Explanations Section */}
            <section className="sales-section explanations-section">
              <div className="content-with-image reverse">
                <div className="image-side">
                  <img src={frcophth1QuestionsPage} alt="FRCOphth Part 1 Question with Explanation" className="product-screenshot" />
                </div>
                <div className="content-side">
                  <div className="section-header">
                    <FaBrain className="section-icon" />
                    <h2>Learn Faster with Clear Explanations</h2>
                  </div>
                  <p className="lead-text">
                    Every question comes with detailed explanations that help you truly understand the material. You'll see:
                  </p>
                  <div className="simple-list">
                    <div className="list-item">
                      <span className="bullet"></span>
                      <p>A clear explanation of the correct answer</p>
                    </div>
                    <div className="list-item">
                      <span className="bullet"></span>
                      <p>Reasons why the other options are incorrect</p>
                    </div>
                    <div className="list-item">
                      <span className="bullet"></span>
                      <p>Exam-relevant teaching points to reinforce learning</p>
                    </div>
                  </div>
                  <p className="emphasis-text">
                    You don't just practise — you understand.
                  </p>
                </div>
              </div>
            </section>

            {/* Analytics Section */}
            <section className="sales-section analytics-section">
              <div className="content-with-image">
                <div className="content-side">
                  <div className="section-header">
                    <FaChartLine className="section-icon" />
                    <h2>Track Progress. Target Weaknesses. Improve Faster.</h2>
                  </div>
                  <p className="lead-text">
                    Our built-in analytics help you monitor performance by topic, identify weak areas instantly, and focus revision where it matters most.
                  </p>
                  <p className="tagline-text">
                    Study smarter — not longer.
                  </p>
                </div>
                <div className="image-side">
                  <img src={frcophth1ModuleProgress} alt="FRCOphth Part 1 Module Progress Analytics" className="product-screenshot" />
                </div>
              </div>
            </section>

            {/* Exam Success Section */}
            <section className="sales-section exam-success-section">
              <div className="content-with-image reverse">
                <div className="image-side">
                  <img src={frcophth1TimedExamPage} alt="FRCOphth Part 1 Timed Exam Practice" className="product-screenshot" />
                </div>
                <div className="content-side">
                  <div className="section-header">
                    <FaBullseye className="section-icon" />
                    <h2>Built for Exam Day Success</h2>
                  </div>
                  <p className="lead-text">
                    Use <strong>timed practice</strong> and full <strong>mock exams</strong> to rehearse under the same
                    pressure as the hall—so speed, stamina, and accuracy improve together. Questions mirror{" "}
                    <strong>real exam wording and format</strong>, not generic MCQ style.
                  </p>
                  <p className="emphasis-text">
                    Walk into your exam with confidence.
                  </p>
                </div>
              </div>
            </section>

            {/* Pricing Section */}
            <section className="sales-section pricing-section">
              <div className="pricing-card">
                <h3>FRCOphth Part 1 Question Bank</h3>
                <div className="pricing-amount">
                  <span className="price-large">£{product.price}</span>
                  <span className="price-period">— 6 months full access</span>
                </div>
                <div className="pricing-includes">
                  <h4>Includes:</h4>
                  <ul>
                    <li><Checkmark /> 3,750+ exam-style MCQs (every module &amp; area)</li>
                    <li><Checkmark /> 500+ high-yield exam questions</li>
                    <li><Checkmark /> Timed practice &amp; full mock exams</li>
                    <li><Checkmark /> Detailed explanations &amp; performance analytics</li>
                  </ul>
                </div>
                <p className="pricing-note">
                  No hidden fees. No unnecessary extras.
                </p>
                <div className="pricing-cta">
                  <Button
                    text="Get Started Now"
                    linkTo={`/sign-up?product=${product.id}`}
                    type="navy"
                  />
                </div>
              </div>
            </section>

          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default FRCOphthPart1Page;

